import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Building, Pickaxe, Clock, MapPin, Users, Sparkles,
    FileText, Image as ImageIcon, RefreshCw, ChevronDown, ChevronRight,
    XCircle, AlertTriangle, Info
} from 'lucide-react';
import type { BackgroundCounts, BackgroundType } from './types';
import {
    getModelBadgeVariant, getModelShortName, shouldShowBookModelBadge,
    shouldShowPassageModelBadges,
    calculateProPercentage, isBookEmpty, isPro
} from './utils';
import type { VerificationIssue } from '../AdminVerificationContext';

interface AdminBackgroundBookCardProps {
    book: string;
    counts: BackgroundCounts;
    hasOutline: boolean;
    isExpanded: boolean;
    onToggleExpand: (book: string) => void;
    onGenerate: (book: string, type: BackgroundType, onlyMissing?: boolean, overrideModel?: string) => void;
    onGenerateOutline: (book: string) => void;
    onGenerateCulturalPrompts: (book: string) => void;
    onGenerateCulturalImages: (book: string) => void;
    onBulkUpgrade: (book: string) => void;

    // Split generatingStates into individual props for better memoization
    isCulturalBookGenerating: boolean;
    isCulturalPassageGenerating: boolean;
    isArchBookGenerating: boolean;
    isArchPassageGenerating: boolean;
    isHistBookGenerating: boolean;
    isHistPassageGenerating: boolean;
    isGeoGenerating: boolean;
    isPeoplePassageGenerating: boolean;
    isLiteraryPassageGenerating: boolean;
    isLiteraryBookGenerating: boolean;
    isOutlineGenerating: boolean;
    isPromptsGenerating: boolean;
    isImagesGenerating: boolean;
    isStudyGuideGenerating: boolean;
    isGuidedStudyGenerating: boolean;

    // Split culturalImageStats
    imgTotal: number;
    imgCompleted: number;
    imgPromptsCompleted: number;
    imgTotalPassages: number;

    currentModel: string;
    generationProgress?: string;
    verificationIssues?: VerificationIssue[];
}

export const AdminBackgroundBookCard = React.memo(({
    book,
    counts,
    currentModel,
    hasOutline,
    isExpanded,
    onToggleExpand,
    onGenerate,
    onGenerateOutline,
    onGenerateCulturalPrompts,
    onGenerateCulturalImages,
    onBulkUpgrade,

    isCulturalBookGenerating,
    isCulturalPassageGenerating,
    isArchBookGenerating,
    isArchPassageGenerating,
    isHistBookGenerating,
    isHistPassageGenerating,
    isGeoGenerating,
    isPeoplePassageGenerating,
    isLiteraryPassageGenerating,
    isLiteraryBookGenerating,
    isOutlineGenerating,
    isPromptsGenerating,
    isImagesGenerating,
    isStudyGuideGenerating,
    isGuidedStudyGenerating,

    imgTotal,
    imgCompleted,
    imgPromptsCompleted,
    imgTotalPassages,

    generationProgress,
    verificationIssues = []
}: AdminBackgroundBookCardProps) => {
    const proPercentage = calculateProPercentage(counts);
    const isEmpty = isBookEmpty(counts);


    const errors = verificationIssues.filter(i => i.type === 'error');
    const warnings = verificationIssues.filter(i => i.type === 'warning');

    const datasetsForBulk: BackgroundType[] = [
        'culturalPassage', 'archaeologicalPassage', 'historicalPassage',
        'literaryPassage', 'peoplePassage', 'geographic', 'studyGuidePassage'
    ];

    const totalUpgradeCount = datasetsForBulk.reduce((acc, type) => {
        const passageCount = (counts as any)[type] || 0;
        const missingCount = counts.totalPassages - passageCount;

        const passageModelMap = (counts as any)[`${type}ByModel`] as Record<string, number> | undefined;
        let nonProCount = 0;
        if (passageModelMap) {
            nonProCount = Object.entries(passageModelMap)
                .filter(([model]) => !isPro(model))
                .reduce((sum, [_, count]) => sum + count, 0);
        }
        return acc + nonProCount + missingCount;
    }, 0);

    const hasAnyNeedsUpgrade = totalUpgradeCount > 0;

    const isAnyCoreGenerating = isCulturalPassageGenerating || isArchPassageGenerating ||
        isHistPassageGenerating || isGeoGenerating || isPeoplePassageGenerating ||
        isLiteraryPassageGenerating || isStudyGuideGenerating;

    // Color logic for Pro progress
    const getProgressBarColor = (pct: number) => {
        if (pct === 0 && isEmpty) return 'bg-muted';
        if (pct < 40) return 'bg-red-500';
        if (pct < 100) return 'bg-orange-500';
        return 'bg-green-600';
    };

    const getIssueIcon = (type: string) => {
        switch (type) {
            case 'error':
                return <XCircle className="h-3 w-3 text-red-500" />;
            case 'warning':
                return <AlertTriangle className="h-3 w-3 text-orange-500" />;
            case 'info':
                return <Info className="h-3 w-3 text-blue-500" />;
            default:
                return null;
        }
    };

    const renderDatasetRow = ({
        label,
        bookType,
        passageType,
        icon: Icon,
        isBookGenerating = false,
        isPassageGenerating = false,
        extraControls
    }: {
        label: string;
        bookType?: BackgroundType;
        passageType: BackgroundType;
        icon: any;
        isBookGenerating?: boolean;
        isPassageGenerating?: boolean;
        extraControls?: React.ReactNode;
    }) => {
        const bookCount = bookType ? (counts as any)[bookType] : undefined;
        const bookModel = bookType ? (counts as any)[`${bookType}Model`] : undefined;
        const passageCount = (counts as any)[passageType];
        const passageModelMap = (counts as any)[`${passageType}ByModel`] as Record<string, number> | undefined;

        const needsBookUpgrade = bookType && bookCount > 0 && bookModel && !isPro(bookModel);

        let needsPassageUpgrade = false;
        if (passageCount > 0 && passageModelMap) {
            for (const model of Object.keys(passageModelMap)) {
                if (!isPro(model)) {
                    needsPassageUpgrade = true;
                    break;
                }
            }
        }

        return (
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium w-24 text-muted-foreground">{label}:</span>

                {/* Book Button */}
                {bookType !== undefined && (
                    <>
                        <Button
                            size="sm"
                            variant={bookCount === 0 ? "default" : "outline"}
                            onClick={() => onGenerate(book, bookType)}
                            disabled={isBookGenerating}
                            className="text-xs h-7"
                        >
                            {isBookGenerating ? (
                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                                <Icon className="h-3 w-3 mr-1" />
                            )}
                            Book ({bookCount.toLocaleString()})
                        </Button>
                        {shouldShowBookModelBadge(bookModel) && (
                            <Badge
                                variant={getModelBadgeVariant(bookModel || '')}
                                className={`text-xs h-5 ${bookModel?.includes('3.1-pro') ? 'border-blue-400/30 text-blue-600 dark:text-blue-400 bg-blue-50/30 dark:bg-blue-950/20' : ''}`}
                            >
                                {getModelShortName(bookModel || '')}
                            </Badge>
                        )}
                        {needsBookUpgrade && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => onGenerate(book, bookType, false, 'gemini-3.1-pro-preview')}
                                disabled={isBookGenerating}
                                className="text-[10px] h-6 px-1 flex items-center text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                                <Sparkles className="h-3 w-3 mr-0.5" />
                                Upgrade
                            </Button>
                        )}
                    </>
                )}

                {/* Passage Button */}
                <Button
                    size="sm"
                    variant={passageCount === 0 ? "default" : "outline"}
                    onClick={() => onGenerate(book, passageType)}
                    disabled={isPassageGenerating}
                    className={`text-xs h-7 ${passageCount > 0 && passageCount < counts.totalPassages ? 'border-orange-500 text-orange-700 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-950' : ''}`}
                >
                    {isPassageGenerating ? (
                        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                        <Icon className="h-3 w-3 mr-1" />
                    )}
                    Passages ({passageCount.toLocaleString()} / {counts.totalPassages.toLocaleString()})
                </Button>

                {/* Passage Model Badges */}
                {shouldShowPassageModelBadges(passageModelMap) && (
                    <div className="flex gap-1">
                        {Object.entries(passageModelMap || {}).map(([modelName, count]) => (
                            <Badge
                                key={modelName}
                                variant={getModelBadgeVariant(modelName)}
                                className={`text-xs h-5 ${modelName.includes('3.1-pro') ? 'border-blue-400/30 text-blue-600 dark:text-blue-400 bg-blue-50/30 dark:bg-blue-950/20' : ''}`}
                            >
                                {getModelShortName(modelName)}: {count.toLocaleString()}
                            </Badge>
                        ))}
                    </div>
                )}

                {/* Generate Missing Button */}
                {passageCount > 0 && passageCount < counts.totalPassages && (
                    <Button
                        size="sm"
                        variant="default"
                        onClick={() => onGenerate(book, passageType, true)}
                        disabled={isPassageGenerating}
                        className="text-xs h-7 bg-orange-600 hover:bg-orange-700"
                    >
                        {isPassageGenerating ? (
                            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                            <Icon className="h-3 w-3 mr-1" />
                        )}
                        Generate Missing ({(counts.totalPassages - passageCount).toLocaleString()})
                    </Button>
                )}

                {/* Passage Upgrade Button */}
                {needsPassageUpgrade && (
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onGenerate(book, passageType, false, 'gemini-3.1-pro-preview')}
                        disabled={isPassageGenerating}
                        className="text-[10px] h-6 px-1 flex items-center text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                        <Sparkles className="h-3 w-3 mr-0.5" />
                        Upgrade
                    </Button>
                )}


                {extraControls}
            </div>
        );
    };

    return (
        <Card className="border-l-4 p-0" style={{
            borderLeftColor: errors.length > 0 ? '#ef4444' : warnings.length > 0 ? '#f59e0b' : proPercentage === 100 ? '#16a34a' : proPercentage >= 40 ? '#f59e0b' : '#ef4444'
        }}>
            <CardContent className="p-1.5 sm:p-2">
                {/* Header Row */}
                <div className={`flex items-center justify-between ${isExpanded ? 'mb-2 sm:mb-3' : 'mb-0'} gap-2`}>
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onToggleExpand(book)}
                            className="p-0 h-auto flex-shrink-0"
                        >
                            {isExpanded ? (
                                <ChevronDown className="h-5 w-5 sm:h-6 sm:w-6" />
                            ) : (
                                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
                            )}
                        </Button>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <h3 className="text-base sm:text-lg font-bold truncate">{book}</h3>
                                {errors.length > 0 && (
                                    <Badge variant="destructive" className="h-5 px-1.5 flex items-center gap-1">
                                        <XCircle className="h-3 w-3" />
                                        {errors.length}
                                    </Badge>
                                )}
                                {warnings.length > 0 && (
                                    <Badge variant="secondary" className="h-5 px-1.5 flex items-center gap-1 bg-orange-100 text-orange-700 hover:bg-orange-100 border-none">
                                        <AlertTriangle className="h-3 w-3" />
                                        {warnings.length}
                                    </Badge>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-20 sm:w-32 bg-muted rounded-full h-2 flex-shrink-0 overflow-hidden border dark:border-slate-800">
                                    <div
                                        className={`h-full transition-all duration-500 ${getProgressBarColor(proPercentage)}`}
                                        style={{ width: `${proPercentage}%` }}
                                    />
                                </div>
                                <span className="text-[10px] sm:text-xs font-semibold text-muted-foreground whitespace-nowrap">
                                    {proPercentage.toFixed(0)}% Pro
                                </span>
                            </div>
                        </div>
                    </div>

                    {hasAnyNeedsUpgrade && (
                        <Button
                            size="sm"
                            variant="default"
                            onClick={(e) => {
                                e.stopPropagation();
                                onBulkUpgrade(book);
                            }}
                            disabled={isAnyCoreGenerating}
                            className="text-[10px] sm:text-xs h-7 bg-blue-600 hover:bg-blue-700 whitespace-nowrap shadow-sm"
                        >
                            {isAnyCoreGenerating ? (
                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                                <Sparkles className="h-3 w-3 mr-1" />
                            )}
                            Upgrade Pro ({totalUpgradeCount})
                        </Button>
                    )}
                </div>

                {/* Outline Status - Only show container if expanded or has something important */}
                {isExpanded && (
                    <div className="flex items-center gap-1 sm:gap-2 h-8 mb-2">
                        <Button
                            size="sm"
                            variant={hasOutline ? "outline" : "default"}
                            onClick={() => {
                                if (window.confirm(`Are you sure you want to ${hasOutline ? 'regenerate' : 'generate'} the outline for ${book}? This will reset the section structure.`)) {
                                    onGenerateOutline(book);
                                }
                            }}
                            disabled={isOutlineGenerating}
                            className={`text-[10px] sm:text-xs h-7 px-2 sm:px-3 ${!hasOutline ? 'animate-pulse' : ''}`}
                        >
                            {isOutlineGenerating ? (
                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                                <FileText className="h-3 w-3 mr-1" />
                            )}
                            {hasOutline ? 'Outline' : 'Gen Outline'}
                        </Button>
                    </div>
                )}

                {/* Expanded Content */}
                {isExpanded && (
                    <div className="mt-4 pt-4 border-t space-y-4">
                        {/* Verification Issues Section */}
                        {verificationIssues.length > 0 && (
                            <div className="bg-red-50/30 dark:bg-red-900/10 rounded-lg p-3 border border-red-100 dark:border-red-900/30">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                                    <h4 className="text-sm font-semibold">Verification Issues</h4>
                                </div>
                                <div className="space-y-1.5">
                                    {verificationIssues.map((issue, idx) => (
                                        <div key={idx} className="flex items-start gap-2 text-[11px] sm:text-xs">
                                            <span className="mt-0.5">{getIssueIcon(issue.type)}</span>
                                            <div className="flex-1">
                                                <div className="font-medium text-foreground">{issue.message}</div>
                                                {issue.details && (
                                                    <div className="text-[10px] text-muted-foreground font-mono mt-0.5 opacity-70">
                                                        {JSON.stringify(issue.details)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {renderDatasetRow({
                            label: 'Literary',
                            bookType: 'literaryBook',
                            passageType: 'literaryPassage',
                            icon: Sparkles,
                            isBookGenerating: isLiteraryBookGenerating,
                            isPassageGenerating: isLiteraryPassageGenerating
                        })}

                        {renderDatasetRow({
                            label: 'Cultural',
                            bookType: 'culturalBook',
                            passageType: 'culturalPassage',
                            icon: Building,
                            isBookGenerating: isCulturalBookGenerating,
                            isPassageGenerating: isCulturalPassageGenerating,
                            extraControls: counts.culturalPassage > 0 && (
                                <>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => onGenerateCulturalPrompts(book)}
                                        disabled={isPromptsGenerating || isImagesGenerating}
                                        className="text-xs h-7 border-blue-500 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950"
                                    >
                                        {isPromptsGenerating ? (
                                            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                        ) : (
                                            <FileText className="h-3 w-3 mr-1" />
                                        )}
                                        {isPromptsGenerating
                                            ? generationProgress || 'Prompts...'
                                            : `Prompts (${imgPromptsCompleted.toLocaleString()}/${(imgTotalPassages || counts.totalPassages).toLocaleString()})`
                                        }
                                    </Button>

                                    <Button
                                        size="sm"
                                        variant={imgCompleted === imgTotal && imgTotal > 0 ? "outline" : "default"}
                                        onClick={() => onGenerateCulturalImages(book)}
                                        disabled={isPromptsGenerating || isImagesGenerating}
                                        className="text-xs h-7 border-blue-500 text-blue-700 dark:text-blue-400"
                                    >
                                        {isImagesGenerating ? (
                                            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                        ) : (
                                            <ImageIcon className="h-3 w-3 mr-1" />
                                        )}
                                        {isImagesGenerating
                                            ? generationProgress || 'Images...'
                                            : imgTotal > 0
                                                ? `Images (${imgCompleted.toLocaleString()}/${imgTotal.toLocaleString()})`
                                                : 'Gen Images'
                                        }
                                    </Button>
                                </>
                            )
                        })}

                        {renderDatasetRow({
                            label: 'Archaeological',
                            bookType: 'archaeologicalBook',
                            passageType: 'archaeologicalPassage',
                            icon: Pickaxe,
                            isBookGenerating: isArchBookGenerating,
                            isPassageGenerating: isArchPassageGenerating
                        })}

                        {renderDatasetRow({
                            label: 'Historical',
                            bookType: 'historicalBook',
                            passageType: 'historicalPassage',
                            icon: Clock,
                            isBookGenerating: isHistBookGenerating,
                            isPassageGenerating: isHistPassageGenerating
                        })}

                        {renderDatasetRow({
                            label: 'Geographic',
                            passageType: 'geographic',
                            icon: MapPin,
                            isPassageGenerating: isGeoGenerating
                        })}

                        {renderDatasetRow({
                            label: 'People',
                            passageType: 'peoplePassage',
                            icon: Users,
                            isPassageGenerating: isPeoplePassageGenerating
                        })}

                        {renderDatasetRow({
                            label: 'Study Guide',
                            passageType: 'studyGuidePassage',
                            icon: FileText,
                            isPassageGenerating: isStudyGuideGenerating
                        })}

                        {renderDatasetRow({
                            label: 'Guided Study',
                            passageType: 'guidedStudyPassage',
                            icon: FileText,
                            isPassageGenerating: isGuidedStudyGenerating
                        })}
                    </div>
                )}
            </CardContent>
        </Card >
    );
});

AdminBackgroundBookCard.displayName = 'AdminBackgroundBookCard';

